// ────────── Design tokens — from Claude Design prototype ──────────

export const VColor = {
  bg: '#FAF7F2',
  paper: '#FFFFFF',
  ink: '#171513',
  ink2: '#3A332C',
  muted: '#8A8278',
  line: '#E8E1D6',
  line2: '#F0EADD',
  accent: '#0E6F66',
  accentSoft: '#D5EAE6',
  accentInk: '#0A5A52',
  good: '#157F4D',
  goodSoft: '#DCEFE3',
  warn: '#B5610B',
  warnSoft: '#F5E5CD',
  bad: '#B53729',
  badSoft: '#F5DAD4',
  cream: '#F4EEDF',
} as const;

export const VFont = {
  sans: '"Geist", "Inter", -apple-system, system-ui, sans-serif',
  serif: '"Instrument Serif", "Cormorant Garamond", "Times New Roman", serif',
  mono: '"Geist Mono", "JetBrains Mono", ui-monospace, monospace',
} as const;

// ────────── Verdict system ──────────

export function verdictTone(v: string | null | undefined): { fg: string; mark: string } {
  if (!v) return { fg: VColor.muted, mark: VColor.muted };
  const k = v.toUpperCase();
  if (k.includes('RARE') || k.includes('EXCEPT')) return { fg: VColor.ink, mark: VColor.good };
  if (k.includes('AVANT') || k.includes('BON')) return { fg: VColor.ink, mark: VColor.good };
  if (k.includes('AU MARCH') || k.includes('STANDARD') || k.includes('CORRECT')) return { fg: VColor.ink2, mark: VColor.muted };
  if (k.includes('TRÈS') || k.includes('EXCESS') || k.includes('SURPAY')) return { fg: VColor.bad, mark: VColor.bad };
  if (k.includes('ÉLEV') || k.includes('CHER')) return { fg: VColor.warn, mark: VColor.warn };
  return { fg: VColor.muted, mark: VColor.muted };
}

// ────────── Confidence system ──────────

export type ConfidenceLevel = 'haute' | 'moyenne' | 'fragile';

export function confidenceFromEmoji(emoji: string): ConfidenceLevel {
  if (emoji === '\u{1F7E2}') return 'haute';
  if (emoji === '\u{1F7E1}') return 'moyenne';
  if (emoji === '\u{1F534}') return 'fragile';
  // V4.1 Fix 3.2 : confiance inconnue = moyenne (pas haute — ne pas rassurer à tort)
  return 'moyenne';
}

export function confidenceInfo(level: ConfidenceLevel) {
  const map: Record<ConfidenceLevel, { label: string; tone: string; soft: string }> = {
    haute: { label: 'Haute confiance', tone: VColor.good, soft: VColor.goodSoft },
    moyenne: { label: 'Confiance moyenne', tone: VColor.warn, soft: VColor.warnSoft },
    fragile: { label: 'Confiance fragile', tone: VColor.bad, soft: VColor.badSoft },
  };
  return map[level] || map.haute;
}

// ────────── Verdict extraction from API message ──────────

export function extractVerdictTier(msg: string): string {
  if (!msg) return 'AU MARCHÉ';
  const upper = msg.toUpperCase();
  if (upper.includes('EXCEPTIONNEL') || upper.includes('RARE')) return 'RARE';
  if (upper.includes('BON PRIX') || upper.includes('AVANTAGEUX')) return 'AVANTAGEUX';
  if (upper.includes('CORRECT') || upper.includes('STANDARD') || upper.includes('MÉDIANE')) return 'AU MARCHÉ';
  if (upper.includes('CHER') || upper.includes('ÉLEVÉ')) return 'ÉLEVÉ';
  if (upper.includes('EXCESSIF') || upper.includes('SURPAY')) return 'EXCESSIF';
  return 'AU MARCHÉ';
}

// ────────── Flag mapper ──────────

export type FlagKind = 'destructive' | 'info';

export function mapFlag(flag: string): { kind: FlagKind; label: string } {
  const lower = flag.toLowerCase();
  // V3.2 P2 : liste exhaustive des mots-clés destructifs (cohérente avec anti_piege.py)
  const destructiveKeywords = [
    'soute', 'payant', 'supplément', 'low-cost',
    'loin', 'éloigné', 'excentré', 'navette',
    'nuit', 'nocturne', 'matinal',
    'correspondance courte', 'self-transfer', 'escale',
    'annulation non', 'non gratuite',
    'frais de ménage', 'ménage élevé',
    'très bas', 'light/basic', 'basic',
    'taxe de séjour non',
    'très long',
  ];
  if (destructiveKeywords.some(kw => lower.includes(kw)))
    return { kind: 'destructive', label: flag };
  return { kind: 'info', label: flag };
}

// ────────── Budget tone ──────────

export function budgetTone(pct: number): string {
  if (pct < 50) return VColor.good;
  if (pct < 80) return VColor.warn;
  return VColor.bad;
}

// ────────── API URL ──────────

// V4.1 Fix 2.6 : configurable via NEXT_PUBLIC_API_URL (réseau local, OVH, etc.)
export const API_URL = "";
